import React, { useState, useRef, KeyboardEvent, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';

/** In-flight attachment state, rendered as a slim progress row above the composer. */
export interface ChatAttachmentProgress {
  fileName: string;
  /** 0..100 while bytes are in flight; null while the server is parsing the file. */
  progress: number | null;
}

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  /** When set, a paperclip button + drop target let the user attach a file (one at a time). */
  onAttachFile?: (file: File) => void;
  /** `accept` for the hidden file input, e.g. ".xlsx,.xls,.csv". */
  attachAccept?: string;
  /** Accessible name / tooltip for the paperclip. */
  attachLabel?: string;
  /** Attachment in progress: disables the paperclip and shows the progress row. */
  attachment?: ChatAttachmentProgress | null;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  isLoading = false,
  placeholder,
  onAttachFile,
  attachAccept,
  attachLabel,
  attachment,
}) => {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder || t('Ask me anything...');
  const [message, setMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const canAttach = !!onAttachFile && !disabled && !isLoading && !attachment;

  const handleSend = () => {
    if (message.trim() && !disabled && !isLoading) {
      onSendMessage(message);
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        // Keep the caret in the composer so the user can keep typing without re-clicking.
        textareaRef.current.focus();
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 96)}px`;
  };

  const pickFile = (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (file && onAttachFile) onAttachFile(file);
  };

  // Drop target is the composer itself. Only file drags light it up; text drags fall through.
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!canAttach || !Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragOver(false);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!canAttach) return;
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files);
  };

  return (
    <div
      className={cn('border-t border-border bg-card transition-colors', dragOver && 'bg-primary/5')}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {attachment && (
        <div className="flex items-center gap-2 px-3 pt-2 text-xs text-muted-foreground" role="status" aria-live="polite">
          <Icon name="Loader2" size={13} className="animate-spin text-primary flex-shrink-0" />
          <span className="min-w-0 truncate">
            {attachment.progress === null
              ? t('Reading {{fileName}}…', { fileName: attachment.fileName })
              : t('Uploading {{fileName}}… {{pct}}%', { fileName: attachment.fileName, pct: attachment.progress })}
          </span>
          {attachment.progress !== null && (
            <span
              className="ms-auto h-1 w-24 flex-shrink-0 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={attachment.progress}
              aria-label={t('Upload progress')}
            >
              <span className="block h-full rounded-full bg-primary transition-[width] duration-200" style={{ width: `${attachment.progress}%` }} />
            </span>
          )}
        </div>
      )}
      <div className="flex gap-2 p-3">
        {onAttachFile && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={attachAccept}
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                pickFile(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={!canAttach}
              aria-label={attachLabel || t('Attach a file')}
              title={attachLabel || t('Attach a file')}
              className="flex-shrink-0 w-10 h-10 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Icon name="Paperclip" size={18} />
            </button>
          </>
        )}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={dragOver ? t('Drop the sheet to upload it') : resolvedPlaceholder}
          aria-label={t('Message the assistant')}
          disabled={disabled || isLoading}
          rows={1}
          className={cn(
            'flex-1 resize-none rounded-lg border bg-muted px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus:outline-none hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
            dragOver ? 'border-primary border-dashed' : 'border-border',
          )}
          style={{ maxHeight: '96px', overflowY: 'auto' }}
        />
        <button
          onClick={handleSend}
          disabled={!message.trim() || disabled || isLoading}
          aria-label={t('Send message')}
          title={t('Send message')}
          className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground flex items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {isLoading ? (
            <Icon name="Loader2" size={20} className="animate-spin" />
          ) : (
            <Icon name="Send" size={20} />
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
